import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowPathIcon, CalendarIcon } from '@heroicons/react/24/solid';
import SportScores from './components/SportScores';

// Configure axios defaults based on environment
// In production (Azure), use relative URLs; in development, use localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' ? 'http://localhost:5000' : '');
axios.defaults.baseURL = API_BASE_URL;

const SPORTS = ['MLB', 'NHL', 'NBA', 'NFL'];

function App() {
  const [scoresData, setScoresData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showToday, setShowToday] = useState(true);
  
  // Get rundate from URL parameters
  const getRundateFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('rundate');
  };

  const getFormattedDate = (offset = 0, rundate = null) => {
    if (rundate) return rundate;

    // Get current time in Eastern Time, then subtract 8 hours so that
    // early morning hours (e.g. 2 AM ET) still show the previous night's games
    const now = new Date();
    const easternHour = parseInt(now.toLocaleString('en-US', { timeZone: 'America/New_York', hour: 'numeric', hour12: false }));
    const shiftedOffset = easternHour < 8 ? offset - 1 : offset;

    const easternDateString = now.toLocaleDateString('en-CA', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });

    const easternDate = new Date(easternDateString + 'T00:00:00');
    easternDate.setDate(easternDate.getDate() + shiftedOffset);

    const year = easternDate.getFullYear();
    const month = String(easternDate.getMonth() + 1).padStart(2, '0');
    const day = String(easternDate.getDate()).padStart(2, '0');

    return `${year}${month}${day}`;
  };

  const fetchScores = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const rundate = getRundateFromUrl();
      const date = rundate ? getFormattedDate(0, rundate) : (showToday ? getFormattedDate() : getFormattedDate(-1));
      const newScoresData = {};
      
      console.log('Fetching scores for date:', date);
      console.log('API Base URL:', API_BASE_URL);

      // Fetch scores for all sports in parallel
      await Promise.all(SPORTS.map(async (sport) => {
        let apiEndpoint;
        try {
          if (sport === 'MLB') {
            apiEndpoint = `/api/scores/mlb?date=${date}`;
          } else if (sport === 'NFL') {
            apiEndpoint = `/api/scores/nfl?date=${date}`;
          } else {
            apiEndpoint = `/api/scores/${sport}?date=${date}`;
          }
          
          const response = await axios.get(apiEndpoint);
          const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
          
          // Only add sports that have events
          if (data.events && data.events.length > 0) {
            // Calculate totals
            let homeTotal = 0;
            let awayTotal = 0;
            
            data.events.forEach(event => {
              const homeTeam = event.competitions[0].competitors.find(team => team.homeAway === 'home');
              const awayTeam = event.competitions[0].competitors.find(team => team.homeAway === 'away');
              
              homeTotal += parseInt(homeTeam?.score || 0);
              awayTotal += parseInt(awayTeam?.score || 0);
            });

            newScoresData[sport] = {
              scores: data,
              totals: { home: homeTotal, away: awayTotal }
            };
            
            // Fetch NFL totals if this is NFL
            if (sport === 'NFL') {
              try {
                const nflTotalsResponse = await axios.get(`/api/scores/nfl/totals?date=${date}`);
                const nflTotalsData = typeof nflTotalsResponse.data === 'string' ? JSON.parse(nflTotalsResponse.data) : nflTotalsResponse.data;
                newScoresData[sport].nflTotals = nflTotalsData;
              } catch (err) {
                console.error('Error fetching NFL totals:', err);
                newScoresData[sport].nflTotals = null;
              }
            }
          }
        } catch (err) {
          console.error(`Error fetching ${sport} scores:`, err);
          console.error(`Failed endpoint: ${apiEndpoint}`);
        }
      }));

      // Only set scores data if we have any scores to show
      if (Object.keys(newScoresData).length > 0) {
        setScoresData(newScoresData);
        setError(null);
      } else {
        setScoresData({});
        const rundate = getRundateFromUrl();
        const dateLabel = rundate ? `date ${rundate}` : (showToday ? 'today' : 'yesterday');
        setError(`No games scheduled for ${dateLabel}`);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching scores:', err);
      setError('Failed to fetch scores. Please try again later.');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScores();
  }, [showToday]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <ArrowPathIcon className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">

      <header className="bg-gradient-to-r from-blue-700 to-blue-900 shadow-lg">
        <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-2 sm:space-y-0">
            <h1 className="text-base sm:text-lg md:text-xl font-bold text-white">Sports Scoreboard</h1>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
              <button
                onClick={() => setShowToday(!showToday)}
                className="flex items-center justify-center bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-3 py-1.5 rounded-md transition-colors duration-200"
              >
                <CalendarIcon className="w-4 h-4 mr-1.5" />
                {showToday ? "Show Yesterday" : "Show Today"}
              </button>
              <button
                onClick={fetchScores}
                className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-3 py-1.5 rounded-md transition-colors duration-200"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {error ? (
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <p className="text-gray-600 text-lg">{error}</p>
          </div>
        ) : (
          SPORTS.map(sport => (
            scoresData[sport] && (
              <SportScores
                key={sport}
                sport={sport}
                scores={scoresData[sport]?.scores}
                totals={scoresData[sport]?.totals}
                nflTotals={scoresData[sport]?.nflTotals}
              />
            )
          ))
        )}
      </main>
    </div>
  );
}

export default App; 